import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Info, Play as PlayIcon, Pause as PauseIcon, RotateCcw, Zap, Clock, Music2, Image as ImageIcon, Film, Sparkles, Sliders, Tag } from 'lucide-react';
import { SettingsDrawer } from './SettingsDrawer';

const BRAND = '#915ee9';

function fmt(sec) {
  if (sec == null || isNaN(sec)) return '0:00';
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// ============================================================================
// Option catalogues
// ============================================================================
const TRANSITIONS = [
  { id: 'none',      label: 'None',        desc: 'Straight cuts, no blend',  sw: 120, sh: 68, silhouette: 'cut' },
  { id: 'crossfade', label: 'Crossfade',   desc: 'Smooth fade between',      sw: 120, sh: 68, silhouette: 'blend' },
  { id: 'fade',      label: 'Fade to Black', desc: 'Black momentarily',      sw: 120, sh: 68, silhouette: 'fade' },
  { id: 'slide',     label: 'Slide',       desc: 'Slide in from side',       sw: 120, sh: 68, silhouette: 'slide' },
  { id: 'zoom',      label: 'Zoom',        desc: 'Zoom between photos',      sw: 120, sh: 68, silhouette: 'zoom' },
];

const FIT_MODES = [
  { id: 'cover',   label: 'Fill',     desc: 'Fill the whole frame (may crop edges)', silhouette: 'fill' },
  { id: 'contain', label: 'Fit',      desc: 'Show whole image (may letterbox)',      silhouette: 'fit' },
];

const DURATION_PRESETS = [3, 5, 8, 10, 12];

// ============================================================================
// SettingRow — a clickable row that opens its drawer
// ============================================================================
function SettingRow({ icon, label, value, onClick, testId }) {
  const Icon = icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#915ee9]/50 hover:bg-white/[0.04] transition-colors duration-200 text-left cursor-pointer focus:outline-none focus:border-[#915ee9]/70 focus:ring-1 focus:ring-[#915ee9]/40"
    >
      <Icon className="h-4 w-4 text-[#b094f2] flex-shrink-0" />
      <span className="text-sm text-zinc-200 font-medium flex-shrink-0">{label}</span>
      <span className="flex-1 text-right text-sm text-zinc-400 truncate">{value}</span>
      <ChevronRight className="h-4 w-4 text-zinc-500 flex-shrink-0" />
    </button>
  );
}

// ============================================================================
// Silhouettes (simple SVG-free divs for Transitions)
// ============================================================================
function TransitionSilhouette({ kind, selected }) {
  const bg = selected
    ? 'bg-gradient-to-br from-[#915ee9] to-[#7d46d9]'
    : 'bg-zinc-700';
  if (kind === 'cut') {
    return (
      <div className="flex items-center gap-0">
        <div className={`w-14 h-14 rounded-l-md ${bg}`} />
        <div className={`w-14 h-14 rounded-r-md ${bg} opacity-70`} />
      </div>
    );
  }
  if (kind === 'blend') {
    return (
      <div className="relative w-28 h-14">
        <div className={`absolute inset-y-0 left-0 w-20 rounded-md ${bg} opacity-90`} />
        <div className={`absolute inset-y-0 right-0 w-20 rounded-md ${bg} opacity-60`} />
      </div>
    );
  }
  if (kind === 'fade') {
    return (
      <div className="flex items-center gap-1">
        <div className={`w-12 h-14 rounded-l-md ${bg}`} />
        <div className="w-2 h-14 bg-black rounded-sm" />
        <div className={`w-12 h-14 rounded-r-md ${bg}`} />
      </div>
    );
  }
  if (kind === 'slide') {
    return (
      <div className="relative w-28 h-14 overflow-hidden rounded-md">
        <div className={`absolute inset-y-0 left-0 w-16 ${bg}`} />
        <div className={`absolute inset-y-0 left-10 w-16 ${bg} opacity-80 rounded-md`} />
      </div>
    );
  }
  if (kind === 'zoom') {
    return (
      <div className="relative w-28 h-14 rounded-md overflow-hidden flex items-center justify-center">
        <div className={`absolute inset-0 ${bg} opacity-40 rounded-md`} />
        <div className={`w-14 h-10 ${bg} rounded-md`} />
      </div>
    );
  }
  return null;
}

function FitSilhouette({ kind, selected }) {
  const purple = selected ? 'bg-gradient-to-br from-[#915ee9] to-[#7d46d9]' : 'bg-zinc-600';
  // Frame 120x70 (16:9 landscape)
  return (
    <div className="relative w-32 h-20 border-2 border-white/40 rounded-md overflow-hidden bg-zinc-950">
      {kind === 'fill' ? (
        <div className={`absolute -inset-2 ${purple}`} />
      ) : (
        <div className={`absolute inset-3 ${purple} rounded-sm`} />
      )}
    </div>
  );
}

// ============================================================================
// Individual Drawer bodies
// ============================================================================
function TransitionsDrawer({ current, onSelect, onOpenChange }) {
  const handlePick = (id) => {
    onSelect(id);
    setTimeout(() => onOpenChange(false), 120);
  };
  return (
    <div className="grid grid-cols-1 gap-3">
      {TRANSITIONS.map((t) => {
        const selected = t.id === current;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => handlePick(t.id)}
            data-testid={`transition-option-${t.id}`}
            className={`flex items-center gap-4 p-3 rounded-xl border transition-colors ${
              selected
                ? 'border-[#915ee9] bg-[#915ee9]/10'
                : 'border-white/10 bg-white/[0.02] hover:border-[#915ee9]/40'
            }`}
          >
            <div className="flex-shrink-0 w-32 h-14 flex items-center justify-center">
              <TransitionSilhouette kind={t.silhouette} selected={selected} />
            </div>
            <div className="flex-1 text-left">
              <div className="text-white font-medium">{t.label}</div>
              <div className="text-zinc-500 text-xs">{t.desc}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function KenBurnsDrawer({ enabled, onToggle, onOpenChange }) {
  const handle = (next) => {
    onToggle(next);
    setTimeout(() => onOpenChange(false), 120);
  };
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-[#b094f2] flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-white text-sm font-medium">What is Ken Burns?</p>
            <p className="text-zinc-400 text-sm leading-relaxed">
              A slow pan-and-zoom effect applied to each photo. Adds cinematic motion so still images feel alive.
              Named after the documentary filmmaker who popularized it.
            </p>
          </div>
        </div>
      </div>

      {/* Example "thumbs" — animated gradient boxes */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <div className="relative h-24 rounded-lg overflow-hidden bg-zinc-900 border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-br from-[#915ee9]/80 to-[#7d46d9]/80 animate-kb-pan-1" />
          </div>
          <p className="text-xs text-zinc-500 text-center">Slow zoom-in</p>
        </div>
        <div className="space-y-2">
          <div className="relative h-24 rounded-lg overflow-hidden bg-zinc-900 border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-tr from-[#915ee9]/80 to-[#7d46d9]/80 animate-kb-pan-2" />
          </div>
          <p className="text-xs text-zinc-500 text-center">Slow pan</p>
        </div>
      </div>

      <style>{`
        @keyframes kbPan1 { 0% { transform: scale(1) translate(0,0); } 100% { transform: scale(1.15) translate(-4%, -4%); } }
        @keyframes kbPan2 { 0% { transform: scale(1.1) translate(4%, 0); } 100% { transform: scale(1) translate(-4%, 0); } }
        .animate-kb-pan-1 { animation: kbPan1 4s ease-in-out infinite alternate; }
        .animate-kb-pan-2 { animation: kbPan2 4s ease-in-out infinite alternate; }
      `}</style>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => handle(false)}
          data-testid="kb-option-off"
          className={`p-4 rounded-xl border text-center font-medium transition-colors ${
            !enabled ? 'border-[#915ee9] bg-[#915ee9]/10 text-white' : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'
          }`}
        >
          Off
          <div className="text-xs text-zinc-500 mt-1">Static images</div>
        </button>
        <button
          type="button"
          onClick={() => handle(true)}
          data-testid="kb-option-on"
          className={`p-4 rounded-xl border text-center font-medium transition-colors ${
            enabled ? 'border-[#915ee9] bg-[#915ee9]/10 text-white' : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'
          }`}
        >
          On
          <div className="text-xs text-zinc-500 mt-1">Cinematic motion</div>
        </button>
      </div>
    </div>
  );
}

function FitModeDrawer({ current, onSelect, onOpenChange }) {
  const handlePick = (id) => {
    onSelect(id);
    setTimeout(() => onOpenChange(false), 120);
  };
  return (
    <div className="grid grid-cols-2 gap-4">
      {FIT_MODES.map((f) => {
        const selected = f.id === current;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => handlePick(f.id)}
            data-testid={`fit-option-${f.id}`}
            className={`flex flex-col items-center gap-3 p-4 rounded-xl border transition-colors ${
              selected ? 'border-[#915ee9] bg-[#915ee9]/10' : 'border-white/10 bg-white/[0.02] hover:border-[#915ee9]/40'
            }`}
          >
            <FitSilhouette kind={f.silhouette} selected={selected} />
            <div className="text-center">
              <div className="text-white text-sm font-medium">{f.label}</div>
              <div className="text-zinc-500 text-xs mt-0.5">{f.desc}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DurationDrawer({ current, onSelect, onOpenChange }) {
  const handlePick = (val) => {
    onSelect(val);
    setTimeout(() => onOpenChange(false), 120);
  };
  return (
    <div className="space-y-3">
      <p className="text-zinc-400 text-sm">Each image is shown for this many seconds.</p>
      <div className="grid grid-cols-5 gap-2">
        {DURATION_PRESETS.map((s) => {
          const selected = s === current;
          return (
            <button
              key={s}
              type="button"
              onClick={() => handlePick(s)}
              data-testid={`duration-option-${s}`}
              className={`h-16 rounded-xl border text-center font-semibold transition-colors ${
                selected
                  ? 'border-[#915ee9] bg-[#915ee9]/10 text-white'
                  : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'
              }`}
            >
              <div className="text-lg">{s}</div>
              <div className="text-[10px] text-zinc-500">sec</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PerSlideTimingDrawer({ enabled, onToggle, onOpenChange }) {
  const handle = (next) => {
    onToggle(next);
    setTimeout(() => onOpenChange(false), 120);
  };
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-[#b094f2] flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-white text-sm font-medium">Per-slide timing</p>
            <p className="text-zinc-400 text-sm leading-relaxed">
              When on, each image uses its own duration. Set the individual time below the media grid. When off, all images use the Default slide duration.
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button type="button" onClick={() => handle(false)} className={`p-4 rounded-xl border text-center font-medium transition-colors ${!enabled ? 'border-[#915ee9] bg-[#915ee9]/10 text-white' : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'}`}>Off<div className="text-xs text-zinc-500 mt-1">Use default for all</div></button>
        <button type="button" onClick={() => handle(true)} className={`p-4 rounded-xl border text-center font-medium transition-colors ${enabled ? 'border-[#915ee9] bg-[#915ee9]/10 text-white' : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'}`}>On<div className="text-xs text-zinc-500 mt-1">Custom per slide</div></button>
      </div>
    </div>
  );
}

function EndCardDrawer({ enabled, canDisable, onToggle, onOpenChange }) {
  const handle = (next) => {
    onToggle(next);
    setTimeout(() => onOpenChange(false), 120);
  };
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-start gap-3">
          <Tag className="h-5 w-5 text-[#b094f2] flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-white text-sm font-medium">Wrapped In Music end card</p>
            <p className="text-zinc-400 text-sm leading-relaxed">
              A short black closing screen with our logo. {canDisable
                ? 'You can turn this off for your paid video.'
                : 'Required on free videos. Upgrade to a paid render to hide it.'}
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          disabled={!canDisable}
          onClick={() => canDisable && handle(false)}
          data-testid="endcard-option-off"
          className={`p-4 rounded-xl border text-center font-medium transition-colors ${
            !enabled
              ? 'border-[#915ee9] bg-[#915ee9]/10 text-white'
              : canDisable
              ? 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'
              : 'border-white/5 bg-white/[0.02] text-zinc-600 cursor-not-allowed'
          }`}
        >
          Off
          <div className="text-xs text-zinc-500 mt-1">{canDisable ? 'No end card' : 'Paid only'}</div>
        </button>
        <button
          type="button"
          onClick={() => handle(true)}
          data-testid="endcard-option-on"
          className={`p-4 rounded-xl border text-center font-medium transition-colors ${
            enabled ? 'border-[#915ee9] bg-[#915ee9]/10 text-white' : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#915ee9]/40'
          }`}
        >
          On
          <div className="text-xs text-zinc-500 mt-1">Show branding</div>
        </button>
      </div>
    </div>
  );
}

function MusicStartDrawer({ songTitle, audioUrl, songDuration, current, onChange, onOpenChange }) {
  const audioRef = useRef(null);
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [playing, setPlaying] = useState(false);
  const totalDur = Math.max(1, songDuration || 180);
  const pct = Math.min(100, (current / totalDur) * 100);

  useEffect(() => {
    // DO NOT auto-play on open (spec: no noise until desired)
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch (e) { console.debug('audio pause failed on mount:', e); } }
    return () => {
      const b = audioRef.current;
      if (b) { try { b.pause(); } catch (e) { console.debug('audio pause failed on unmount:', e); } }
    };
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
    };
  }, [audioUrl]);

  const posToTime = (clientX) => {
    const t = trackRef.current;
    if (!t) return 0;
    const r = t.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return p * totalDur;
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const next = Math.max(0, Math.min(totalDur, posToTime(clientX)));
      onChange(next);
      const a = audioRef.current;
      if (a && !isNaN(a.duration)) a.currentTime = next;
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, totalDur, onChange]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else {
      try { a.currentTime = current; } catch (e) { console.debug('seek failed:', e); }
      a.play().catch((e) => { console.debug('audio play() blocked:', e); });
    }
  };

  const reset = () => {
    onChange(0);
    const a = audioRef.current;
    if (a) { a.pause(); a.currentTime = 0; }
  };

  // Decorative waveform bars
  const bars = React.useMemo(
    () => Array.from({ length: 60 }, () => 0.35 + Math.random() * 0.65),
    []
  );

  return (
    <div className="space-y-5">
      {audioUrl ? <audio ref={audioRef} src={audioUrl} preload="metadata" /> : null}

      <p className="text-zinc-400 text-sm">
        Drag the handle to pick where the song begins.{' '}
        <span className="text-zinc-500">Audio stays quiet until you tap Play.</span>
      </p>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
        <div className="text-white text-sm font-medium truncate">🎵 {songTitle || 'Selected song'}</div>

        {/* Waveform + scrubber */}
        <div className="relative h-20 select-none" ref={trackRef}
          onMouseDown={(e) => { const t = posToTime(e.clientX); onChange(t); const a = audioRef.current; if (a) a.currentTime = t; setDragging(true); }}
          onTouchStart={(e) => { const t = posToTime(e.touches[0].clientX); onChange(t); const a = audioRef.current; if (a) a.currentTime = t; setDragging(true); }}
        >
          {/* bars */}
          <div className="absolute inset-0 flex items-center gap-[2px] px-1 pointer-events-none">
            {bars.map((h, i) => (
              <div key={i} className="flex-1 rounded-sm" style={{
                height: `${h * 100}%`,
                background: (i / bars.length) * 100 <= pct ? BRAND : 'rgba(255,255,255,0.12)',
              }} />
            ))}
          </div>
          {/* scrubber handle */}
          <div className="absolute top-0 bottom-0 -translate-x-1/2 w-6 flex items-center justify-center" style={{ left: `${pct}%` }}>
            <div className="w-0.5 h-full bg-white" />
            <div className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-md border-2 border-[#915ee9]" />
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500">0:00</span>
          <span className="text-white font-mono font-medium text-sm">Start: {fmt(current)}</span>
          <span className="text-zinc-500">{fmt(totalDur)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlay}
          data-testid="musicstart-play-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#915ee9] hover:bg-[#a070ef] text-white text-sm font-medium transition-colors"
        >
          {playing ? <PauseIcon className="h-3.5 w-3.5" /> : <PlayIcon className="h-3.5 w-3.5" />}
          {playing ? 'Pause' : `Play from ${fmt(current)}`}
        </button>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 text-sm font-medium transition-colors"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset
        </button>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          data-testid="musicstart-apply-btn"
          className="ml-auto px-4 py-2 rounded-md bg-white/5 hover:bg-[#915ee9]/15 hover:border-[#915ee9]/40 border border-white/10 text-white text-sm font-medium transition-colors"
        >
          ✓ Apply
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Settings Panel
// ============================================================================
export default function VideoSettingsPanel({
  // Mode
  isAdvancedMode,
  // Global settings + setters
  transitionType, setTransitionType,
  kenBurnsEnabled, setKenBurnsEnabled,
  musicStartTime, setMusicStartTime,
  globalFitMode, setGlobalFitMode,
  globalDuration, setGlobalDuration,
  usePerSlideTiming, setUsePerSlideTiming,
  endCardEnabled, setEndCardEnabled,
  // Context
  selectedSong,
  videoCost,
}) {
  const [openDrawer, setOpenDrawer] = useState(null); // 'transitions' | 'kb' | 'musicStart' | 'fit' | 'duration' | 'perSlide' | 'endCard' | null
  const [expanded, setExpanded] = useState(true);

  const canDisableEndCard = (videoCost || 0) > 0;
  // If user became "free" again, force end card ON
  useEffect(() => {
    if (!canDisableEndCard && !endCardEnabled) {
      setEndCardEnabled(true);
    }
  }, [canDisableEndCard, endCardEnabled, setEndCardEnabled]);

  const transitionLabel = TRANSITIONS.find(t => t.id === transitionType)?.label || 'Crossfade';
  const fitLabel = FIT_MODES.find(f => f.id === globalFitMode)?.label || 'Fill';

  return (
    <section className="card-standard p-5" data-testid="video-settings-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="video-settings-toggle"
        aria-expanded={expanded}
        className="w-full flex items-center gap-2 text-left group"
      >
        <Sliders className="h-4 w-4 text-[#915ee9] flex-shrink-0" />
        <h2 className="section-title text-base flex-1">Slideshow Settings</h2>
        <span className="helper-text hidden sm:block mr-1 group-hover:text-zinc-300 transition-colors">
          {expanded ? 'Hide' : 'Show'}
        </span>
        <ChevronRight
          className={`h-4 w-4 text-zinc-500 group-hover:text-white transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
      </button>

      {expanded && (
        <div className="flex flex-col gap-2 mt-4">
        {/* Rows visible in BOTH modes */}
        <SettingRow
          icon={Film}
          label="Transitions"
          value={transitionLabel}
          onClick={() => setOpenDrawer('transitions')}
          testId="settings-row-transitions"
        />
        <SettingRow
          icon={Sparkles}
          label="Ken Burns"
          value={kenBurnsEnabled ? 'On' : 'Off'}
          onClick={() => setOpenDrawer('kb')}
          testId="settings-row-kb"
        />

        {/* Advanced-only rows */}
        {isAdvancedMode && (
          <>
            <SettingRow
              icon={Music2}
              label="Music starts at"
              value={fmt(musicStartTime)}
              onClick={() => setOpenDrawer('musicStart')}
              testId="settings-row-musicstart"
            />
            <SettingRow
              icon={ImageIcon}
              label="Image fit"
              value={fitLabel}
              onClick={() => setOpenDrawer('fit')}
              testId="settings-row-fit"
            />
            <SettingRow
              icon={Clock}
              label="Default slide duration"
              value={`${globalDuration}s`}
              onClick={() => setOpenDrawer('duration')}
              testId="settings-row-duration"
            />
            <SettingRow
              icon={Zap}
              label="Per-slide timing"
              value={usePerSlideTiming ? 'On' : 'Off'}
              onClick={() => setOpenDrawer('perSlide')}
              testId="settings-row-perslide"
            />
            <SettingRow
              icon={Tag}
              label="End card"
              value={endCardEnabled ? 'Wrapped In Music' : 'Off'}
              onClick={() => setOpenDrawer('endCard')}
              testId="settings-row-endcard"
            />
          </>
        )}
        </div>
      )}

      {/* Drawers */}
      <SettingsDrawer
        open={openDrawer === 'transitions'}
        onOpenChange={(v) => setOpenDrawer(v ? 'transitions' : null)}
        title="Transitions"
        description="Choose how photos flow into each other."
        testId="drawer-transitions"
      >
        <TransitionsDrawer
          current={transitionType}
          onSelect={setTransitionType}
          onOpenChange={(v) => setOpenDrawer(v ? 'transitions' : null)}
        />
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'kb'}
        onOpenChange={(v) => setOpenDrawer(v ? 'kb' : null)}
        title="Ken Burns effect"
        description="Slow pan & zoom on each photo."
        testId="drawer-kb"
      >
        <KenBurnsDrawer
          enabled={kenBurnsEnabled}
          onToggle={setKenBurnsEnabled}
          onOpenChange={(v) => setOpenDrawer(v ? 'kb' : null)}
        />
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'musicStart'}
        onOpenChange={(v) => setOpenDrawer(v ? 'musicStart' : null)}
        title="Music starts at"
        description="Scrub and listen for the exact spot to begin."
        testId="drawer-musicstart"
      >
        <MusicStartDrawer
          songTitle={selectedSong?.title}
          audioUrl={selectedSong?.audio_url}
          songDuration={selectedSong?.duration}
          current={musicStartTime}
          onChange={setMusicStartTime}
          onOpenChange={(v) => setOpenDrawer(v ? 'musicStart' : null)}
        />
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'fit'}
        onOpenChange={(v) => setOpenDrawer(v ? 'fit' : null)}
        title="Image fit"
        description="How images fill the video frame."
        testId="drawer-fit"
      >
        <FitModeDrawer
          current={globalFitMode}
          onSelect={setGlobalFitMode}
          onOpenChange={(v) => setOpenDrawer(v ? 'fit' : null)}
        />
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'duration'}
        onOpenChange={(v) => setOpenDrawer(v ? 'duration' : null)}
        title="Default slide duration"
        description="Used when per-slide timing is off."
        testId="drawer-duration"
      >
        <DurationDrawer
          current={globalDuration}
          onSelect={setGlobalDuration}
          onOpenChange={(v) => setOpenDrawer(v ? 'duration' : null)}
        />
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'perSlide'}
        onOpenChange={(v) => setOpenDrawer(v ? 'perSlide' : null)}
        title="Per-slide timing"
        description="Set a custom duration for each image."
        testId="drawer-perslide"
      >
        <PerSlideTimingDrawer
          enabled={usePerSlideTiming}
          onToggle={setUsePerSlideTiming}
          onOpenChange={(v) => setOpenDrawer(v ? 'perSlide' : null)}
        />
      </SettingsDrawer>

      <SettingsDrawer
        open={openDrawer === 'endCard'}
        onOpenChange={(v) => setOpenDrawer(v ? 'endCard' : null)}
        title="End card"
        description="Closing Wrapped In Music screen."
        testId="drawer-endcard"
      >
        <EndCardDrawer
          enabled={endCardEnabled}
          canDisable={canDisableEndCard}
          onToggle={setEndCardEnabled}
          onOpenChange={(v) => setOpenDrawer(v ? 'endCard' : null)}
        />
      </SettingsDrawer>
    </section>
  );
}
